import styled, { keyframes, css } from 'styled-components';

/**
 * Auto-Dial Ops Center — chrome.
 * Palette + reusable styled-components shared across page subcomponents.
 * Mirrors IciciOpsCenter's inline-`C` palette pattern for consistency.
 */

export const C = {
  bg:            '#0f1117',
  surface:       '#1a1d26',
  surfaceAlt:    '#1e2230',
  surfaceHi:     '#252938',
  navBg:         '#161921',
  border:        '#2a2d3a',
  borderLight:   '#363b4e',
  purple:        '#8852cc',
  purpleDim:     '#8852cc22',
  purpleSoft:    '#8852cc11',
  green:         '#22c55e',
  greenDim:      '#22c55e1a',
  yellow:        '#f59e0b',
  yellowDim:     '#f59e0b1a',
  red:           '#ef4444',
  redDim:        '#ef44441a',
  blue:          '#3b82f6',
  blueDim:       '#3b82f61a',
  textPrimary:   '#f1f5f9',
  textSecondary: '#cbd5e1',
  textMuted:     '#64748b',
  textDim:       '#475569',
};

export const pulseDot = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.3; }
`;

export const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Page chrome ───────────────────────────────────────────────────────────
// height + overflow-y are set on the Page itself because the app's global
// OuterContainer (App.js) has overflow-y: hidden — without these the page
// would not scroll once content exceeds the viewport.
//
// Block layout (not flex column) is intentional: a flex column with fixed
// height forces children to share the 100vh and shrinks the bottom Panels
// (Deep Dive table collapses to header-only). Block lets children take their
// natural height; the Page itself scrolls when total height > viewport.
export const Page = styled.div`
  width: 100%;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: ${C.bg};
  color: ${C.textPrimary};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  padding: 0 2rem 4rem;
`;

export const TopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 1.4rem;
  height: 5.6rem;
  padding: 0 2rem;
  background: ${C.navBg};
  border-bottom: 1px solid ${C.border};
  margin: 0 -2rem 2.4rem;
  position: sticky;
  top: 0;
  z-index: 200;
  box-shadow: 0 2px 16px rgba(0,0,0,0.45);
`;

export const BackBtn = styled.button`
  background: transparent;
  border: 1px solid ${C.border};
  color: ${C.textMuted};
  border-radius: 0.5rem;
  padding: 0.5rem 1.1rem;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.15s;
  flex-shrink: 0;
  &:hover {
    border-color: ${C.purple};
    color: ${C.purple};
    background: ${C.purpleDim};
  }
`;

export const VisitLogo = styled.img`
  height: 2.4rem;
  width: auto;
  background: #fff;
  padding: 0.25rem 0.7rem;
  border-radius: 0.4rem;
`;

export const LogoDivider = styled.div`
  width: 1px;
  height: 2.2rem;
  background: ${C.border};
`;

export const NavTitle = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${C.textSecondary};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const NavUser = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.2rem;
  color: ${C.textMuted};
  span { color: ${C.textPrimary}; font-weight: 600; }
`;

// ─── Header ────────────────────────────────────────────────────────────────
export const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1.4rem;
  margin-bottom: 2rem;
`;

export const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.02em;
  color: ${C.textSecondary};
  span { color: ${C.purple}; }
`;

export const PageSubtitle = styled.div`
  font-size: 1.2rem;
  color: ${C.textMuted};
  margin-top: 0.4rem;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const HeaderBtn = styled.button`
  background: ${C.surface};
  border: 1px solid ${C.border};
  color: ${C.textSecondary};
  border-radius: 0.6rem;
  padding: 0.7rem 1.2rem;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  transition: all 0.15s;
  &:hover {
    border-color: ${C.purple};
    color: ${C.textPrimary};
    background: ${C.purpleDim};
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const LiveBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${(p) => (p.on ? C.green : C.textMuted)};
  background: ${(p) => (p.on ? C.greenDim : 'transparent')};
  border: 1px solid ${(p) => (p.on ? `${C.green}44` : C.border)};
  padding: 0.4rem 1.1rem;
  border-radius: 9999px;
  letter-spacing: 0.1em;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;
`;

export const LiveDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => p.color || C.green};
  ${(p) => p.pulse && css`animation: ${pulseDot} 1.5s ease-in-out infinite;`}
`;

// ─── Pool toggle ───────────────────────────────────────────────────────────
export const Segmented = styled.div`
  display: inline-flex;
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 9999px;
  padding: 0.3rem;
  gap: 0.2rem;
`;

export const SegmentedItem = styled.button`
  background: ${(p) => (p.active ? C.purple : 'transparent')};
  color: ${(p) => (p.active ? '#fff' : C.textMuted)};
  border: none;
  border-radius: 9999px;
  padding: 0.7rem 1.6rem;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { color: ${(p) => (p.active ? '#fff' : C.textPrimary)}; }
`;

// ─── Panel (used by RightNow / Today / DeepDive) ───────────────────────────
export const Panel = styled.section`
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} 220ms ease-out;
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.4rem 1.6rem;
  border-bottom: 1px solid ${C.border};
`;

export const PanelTitle = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${C.textPrimary};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const PanelSubtitle = styled.div`
  font-size: 1.1rem;
  color: ${C.textMuted};
  margin-top: 0.2rem;
  letter-spacing: 0;
  text-transform: none;
`;

export const PanelBody = styled.div`
  padding: 1.6rem;
  flex: 1;
`;

export const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${(p) => p.color || C.textMuted};
  background: ${(p) => (p.color ? `${p.color}1a` : C.surfaceAlt)};
  border: 1px solid ${(p) => (p.color ? `${p.color}33` : C.border)};
  padding: 0.25rem 0.9rem;
  border-radius: 9999px;
  letter-spacing: 0.05em;
`;

// ─── KPI tile ──────────────────────────────────────────────────────────────
export const StatCard = styled.div`
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-top: 3px solid ${(p) => p.accent || C.purple};
  border-radius: 1rem;
  padding: 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:hover {
    border-color: ${(p) => `${p.accent || C.purple}66`};
    box-shadow: 0 4px 20px ${(p) => `${p.accent || C.purple}18`};
  }
`;

export const StatLabel = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${C.textMuted};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const StatValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
`;

export const StatValue = styled.div`
  font-size: 3.2rem;
  font-weight: 700;
  line-height: 1;
  color: ${(p) => p.color || C.textPrimary};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
`;

export const StatDelta = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${(p) => p.color || C.textMuted};
  font-variant-numeric: tabular-nums;
`;

// ─── Tables ────────────────────────────────────────────────────────────────
export const TableWrap = styled.div`
  overflow-x: auto;
  border-radius: 0 0 1rem 1rem;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1.2rem;

  thead th {
    text-align: left;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${C.textMuted};
    padding: 1rem 1.4rem;
    border-bottom: 1px solid ${C.border};
    background: ${C.surfaceAlt};
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tbody td {
    padding: 1.1rem 1.4rem;
    border-bottom: 1px solid ${C.border};
    color: ${C.textSecondary};
    vertical-align: middle;
  }

  tbody tr:hover {
    background: ${C.surfaceHi};
  }

  tbody tr:last-child td { border-bottom: none; }
`;

export const Empty = styled.div`
  padding: 3.2rem 1.6rem;
  text-align: center;
  color: ${C.textMuted};
  font-size: 1.3rem;
`;

// ─── Status / reason badges ────────────────────────────────────────────────
export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.3rem 0.8rem;
  border-radius: 9999px;
  letter-spacing: 0.04em;
  color: ${(p) => p.color || C.textPrimary};
  background: ${(p) => (p.color ? `${p.color}1a` : C.surfaceAlt)};
  border: 1px solid ${(p) => (p.color ? `${p.color}40` : C.border)};
`;

// ─── Search ────────────────────────────────────────────────────────────────
export const SearchInput = styled.input`
  background: ${C.surfaceAlt};
  border: 1px solid ${C.border};
  border-radius: 0.6rem;
  padding: 0.7rem 1rem;
  font-size: 1.2rem;
  color: ${C.textPrimary};
  width: 24rem;
  transition: border-color 0.15s;
  &::placeholder { color: ${C.textDim}; }
  &:focus {
    outline: none;
    border-color: ${C.purple};
    box-shadow: 0 0 0 3px ${C.purpleDim};
  }
`;
